import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import Paper from 'apollo-react/components/Paper';
import Table from 'apollo-react/components/Table';
import Plus from 'apollo-react-icons/Plus';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ActionCell from './ActionCell';
import ZoomCell from './ZoomCell';
import { DISTRIBUTION_CODE, DISTRIBUTION_NAME } from '../../constants/SmartInsightConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getColumnsWithTranslations } from '../../saved-search-designer/utils/common.utils';
import { getHasSupervisorRight } from '../../selectors/SmartInsightSelectors';

const useStyles = makeStyles({
  addButtonContainer: {
    textAlign: 'right',
    marginTop: '8px !important',
  },
  controlWrapper: {
    position: 'relative',
    margin: '5px 0px',
  },
});

const columns = [
  {
    header: 'Code',
    accessor: DISTRIBUTION_CODE,
    customCell: ({ row, column }) =>
      row.viewOnly ? row[DISTRIBUTION_CODE] : <ZoomCell row={row} column={column} />,
  },
  {
    header: 'Name',
    accessor: DISTRIBUTION_NAME,
  },
  {
    accessor: 'action',
    customCell: ({ row }) => (row.viewOnly ? null : <ActionCell row={row} />),
    align: 'right',
    width: 55,
  },
];

const DistributionTable = ({
  distributionForValueStore,
  distributionListRows,
  handleAddNewDistributionRow,
  handleDeleteDistributionRow,
  handleZoomSelectDistributionList,
  validateDistribution,
  isAdmin,
}) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const hasSupervisorRight = useSelector(getHasSupervisorRight);

  const columnsWithTranslations = useMemo(() => {
    return getColumnsWithTranslations({ columns, translateString });
  }, [translateString]);

  const handleAddNewRow = () => {
    const tmpRowId = uuidv4();

    const newRow = {
      rowId: tmpRowId,
      id: tmpRowId,
      code: '',
      name: '',
    };

    handleAddNewDistributionRow({ newRow });
  };

  return (
    <Grid container spacing={1}>
      <Grid classes={{ item: classes.addButtonContainer }} item xs={12}>
        {hasSupervisorRight && (
          <Button color="primary" variant="secondary" onClick={handleAddNewRow}>
            <Plus fontSize="small" />
            &nbsp;
            {translateString('Add')}
          </Button>
        )}
      </Grid>
      <Grid item xs={12}>
        <Paper square>
          <Table
            columns={columnsWithTranslations}
            emptyProps={{
              text: translateString('No data to display'),
            }}
            rowId="rowId"
            rows={distributionListRows.map((row) => ({
              ...row,
              deleteRow: handleDeleteDistributionRow,
              distributionFor: distributionForValueStore,
              handleZoomSelect: handleZoomSelectDistributionList,
              validate: validateDistribution,
              viewOnly: !hasSupervisorRight,
              translateString,
              classes,
              isAdmin,
            }))}
            rowsPerPageOptions={[5, 10, 20, translateString('All')]}
            defaultRowsPerPage={5}
            showFilterIcon
            tablePaginationProps={{
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} ${translateString('of')} ${count}`,
              labelRowsPerPage: translateString('Rows'),
              pageText: translateString('Page'),
              ofText: translateString('of'),
            }}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DistributionTable;
